<template>
  <v-card>
    <v-form>
      <form-header
        :form="form"
        :title="$lang('documents.title_update')"
        tour="documents-form"
      >
        <template v-slot:left-buttons>
          <v-btn icon dark @click="cancel()">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </template>
        <template v-slot:title>
          <v-chip v-if="$can('INTERNAL_SUPPORT') && !!form.id" color="admin" class="text--primary" small>
            <v-icon small>mdi-identifier</v-icon>
              {{ form.id }}
          </v-chip>
          {{ $lang("documents.title_update") }}
          <v-chip
            v-if="companyName"
            label color="secondary primary--text"
            @click="isEditCompany = !isEditCompany"
          >
            {{ companyName }}
          </v-chip>
        </template>
        <template v-slot:toolbar-items>
          <v-btn
            :disabled="$cannot('DELETE_DOCUMENTS')"
            text
            @click="isEditingDisabled = !isEditingDisabled"
            :loading="loading"
          >
            <v-icon class="mr-1">{{ isEditingDisabled ? 'mdi-lock-open' : 'mdi-lock' }} </v-icon>
            {{ isEditingDisabled ? $lang("main.words.unlock") : $lang("main.words.lock") }}
          </v-btn>
          <v-btn text @click="submit()" :loading="initialized">{{ $lang("main.crud.update") }}</v-btn>
        </template>
      </form-header>
      <v-card-text>
        <v-container>
          <v-row>
            <v-col cols="12" xs="12" md="6">
              <v-row>
                <v-col cols="12" xs="12" md="6" class="mb-0 pb-0">
                  <v-row v-if="isEditCompany">
                    <v-col class="mb-0 pb-0">
                      <c-companies-autocomplete
                        v-model="form.company_id"
                        :label="$lang('documents.fields.company_id')"
                        dense
                        @input="setDocumentSeries()"
                      />
                    </v-col>
                  </v-row>
                  <v-text-field
                    v-model="documentNumber"
                    :label="$lang('documents.fields.id')"
                    :error="!!validation.document_n"
                    :error-messages="!!validation.document_n ? validation.document_n : []"
                    :disabled="!forceEditing && !validation.document_n"
                    dense
                  >
                    <template v-slot:prepend>
                      <v-icon @click="forceEditing = !forceEditing">
                        {{ forceEditing ? 'mdi-lock-open' : 'mdi-lock' }}
                      </v-icon>
                    </template>
                  </v-text-field>
                </v-col>
                <v-col cols="12" xs="12" md="6" class="mb-0 pb-0">
                  <v-select
                    v-model="form.document_series"
                    :label="$lang('documents.fields.series')"
                    :items="series"
                    :error="!!validation.document_series"
                    :error-messages="!!validation.document_series ? validation.document_series : []"
                    @change="seriesChanged"
                    dense
                  />
                </v-col>
                <v-col cols="12" xs="12" md="6" class="mb-0 pb-0">
                  <v-select
                    v-model="form.type"
                    :items="$in('ConstDocumentTypes')"
                    :label="$lang('documents.fields.type')"
                    :error="!!validation.type"
                    :error-messages="!!validation.type ? validation.type : []"
                    disabled
                    dense
                  />
                </v-col>
                <v-col cols="12" xs="12" md="6" class="mb-0 pb-0">
                  <v-date
                    v-model="form.date"
                    :label="$lang('documents.fields.date')"
                    :error="!!validation.date"
                    :error-messages="!!validation.date ? validation.date : []"
                    :disabled="isEditingDisabled"
                    dense
                  />
                </v-col>
                <v-col cols="12" class="mt-0 pt-0">
                  <v-client
                    :item="client"
                    :errors="validation.client_id"
                    @changed="clientChanged"
                  >
                    <template v-slot:info="{ info }"><div></div></template>
                  </v-client>
                </v-col>
              </v-row>
              <v-row v-if="!!form.client_id">
                <v-col cols="12" xs="12" md="6">
                  <v-text-field
                    v-model="form.customer_name"
                    :label="$lang('clients.fields.name')"
                    :error="!!validation.customer_name"
                    :error-messages="!!validation.customer_name ? validation.customer_name : []"
                    dense
                  ></v-text-field>
                  <v-text-field
                    v-model="form.customer_vat_code"
                    :label="$lang('clients.fields.vatcode')"
                    :error="!!validation.customer_vat_code"
                    :error-messages="!!validation.customer_vat_code ? validation.customer_vat_code : []"
                    dense
                  ></v-text-field>
                  <v-text-field
                    v-model="form.customer_tax_code"
                    :label="$lang('clients.fields.taxcode')"
                    :error="!!validation.customer_tax_code"
                    :error-messages="!!validation.customer_tax_code ? validation.customer_tax_code : []"
                    dense
                  ></v-text-field>
                  <v-text-field
                    v-if="isItaDocument"
                    v-model="form.sdi"
                    :label="$lang('clients.fields.sdi')"
                    :error="!!validation.sdi"
                    :error-messages="!!validation.sdi ? validation.sdi : []"
                    dense
                  ></v-text-field>
                  <v-text-field
                    v-if="isItaDocument"
                    v-model="form.pec"
                    :label="$lang('clients.fields.pec')"
                    :error="!!validation.pec"
                    :error-messages="!!validation.pec ? validation.pec : []"
                    dense
                  ></v-text-field>
                </v-col>
                <v-col cols="12" xs="12" md="6">
                  <v-autocomplete
                    v-model="form.customer_country"
                    :items="dataIsoCountries2Letters"
                    :label="$lang('clients.fields.country')"
                    :error="!!validation.customer_country"
                    :error-messages="!!validation.customer_country ? validation.customer_country : []"
                    dense
                  >
                    <template v-slot:item="{item}">
                      <country-flag :country="item.value" size="small" />
                      &nbsp; {{ item.text }}
                    </template>
                  </v-autocomplete>
                  <v-text-field
                    v-model="form.customer_state"
                    :label="$lang('documents.fields.customer_state')"
                    :error="!!validation.customer_state"
                    :error-messages="!!validation.customer_state ? validation.customer_state : []"
                    dense
                  ></v-text-field>
                  <v-text-field
                    v-model="form.customer_city"
                    :label="$lang('clients.fields.city')"
                    :error="!!validation.customer_city"
                    :error-messages="!!validation.customer_city ? validation.customer_city : []"
                    dense
                  ></v-text-field>
                  <v-text-field
                    v-model="form.customer_address"
                    :label="$lang('clients.fields.address')"
                    :error="!!validation.customer_address"
                    :error-messages="!!validation.customer_address ? validation.customer_address : []"
                    dense
                  ></v-text-field>
                  <v-text-field
                    v-model="form.customer_postcode"
                    :label="$lang('clients.fields.postcode')"
                    :error="!!validation.customer_postcode"
                    :error-messages="!!validation.customer_postcode ? validation.customer_postcode : []"
                    dense
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-col>
            <v-col cols="12" xs="12" md="6">
              <v-select
                v-if="$can('INTERNAL_SUPPORT')"
                class="admin"
                v-model="form.status"
                :items="$in('ConstDocumentStatuses')"
                :label="$lang('documents.fields.status')"
                hide-details
                dense
                clearable
              />
              <v-text-field
                v-if="isIta && form.type === $const('ConstDocumentTypes', 'RECEIPT')"
                v-model="form.lottery_code"
                :label="$lang('documents.fields.lottery_code')"
                prepend-icon="mdi-gift"
                dense
              />
              <v-row v-if="form.company_id">
                <v-col cols="12" xs="12" md="6">
                  <c-company-locations-select
                    v-model="form.company_location_id"
                    :label="$lang('company-locations.title')"
                    :company-id="form.company_id"
                    dense
                    clearable
                  />
                </v-col>
                <v-col cols="12" xs="12" md="6">
                  <c-company-journals-select
                    v-model="form.company_journal_id"
                    :label="$lang('company-journals.title')"
                    :company-id="form.company_id"
                    dense
                    clearable
                  />
                </v-col>
              </v-row>
              <v-text-field
                v-if="isIta && (form.type === $const('ConstDocumentTypes', 'RECEIPT') || form.type === $const('ConstDocumentTypes', 'CANCELLATION'))"
                v-model="form.progressivo"
                :label="$lang('documents.fields.entratel_progressivo')"
              ></v-text-field>
              <v-textarea
                v-model="form.notes"
                rows="2"
                filled
                :label="$lang('documents.fields.notes')"
                :error="!!validation.notes"
                :error-messages="!!validation.notes ? validation.notes : []"
              ></v-textarea>
              <v-row v-if="isStampAvailable">
                <v-col cols="3">
                  <v-switch
                    v-model="form.is_stamp_active"
                    :label="$lang('documents.fields.is_stamp_active')"
                  />
                </v-col>
                <v-col cols="9">
                  <v-text-field
                    :disabled="!form.is_stamp_active"
                    v-model="form.stamp_amount"
                    :label="$lang('documents.fields.stamp_amount')"
                    type="number"
                    :suffix="form.currency"
                  />
                </v-col>
              </v-row>
            </v-col>
          </v-row>
          <v-divider></v-divider>
          <v-row>
            <v-col cols="12" xs="12" md="12">
              <v-tabs v-model="tabs" class="mb-2" background-color="primary" dark>
                <v-tab
                  href="#tab-details"
                >{{ $lang('bills.panels.details') }}</v-tab>
                <v-tab
                  href="#tab-payments"
                  :class="totals.remaining_amount > 0 ? 'to-pay' : 'paid'"
                >{{ $lang('bills.panels.payments') }}</v-tab>
              </v-tabs>
              <v-tabs-items v-model="tabs">
                <v-tab-item value="tab-details">
                  <v-simple-table dense>
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th class="text-left" style="width: 50%">{{ $lang('documents.fields.description') }}</th>
                          <th class="text-left" style="width: 12%">{{ $lang('documents.fields.amount') }}</th>
                          <th class="text-left" style="width: 13%">{{ $lang('documents.fields.vat_type') }}</th>
                          <th class="text-left" style="width: 15%">{{ $lang('documents.fields.total') }}</th>
                          <th class="text-center" style="width: 10%">{{ $lang('main.crud.actions') }}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(detail, index) in form.document_details">
                          <td>
                            <v-textarea
                              v-model="detail.description"
                              dense
                              rows="1"
                              :error="!!validation[`document_details.${index}.description`]"
                              :error-messages="!!validation[`document_details.${index}.description`] ? validation[`document_details.${index}.description`] : []"
                              :disabled="isEditingDisabled"
                            />
                          </td>
                          <td>
                            <v-text-field
                              v-model="detail.net_amount"
                              dense
                              :error="!!validation[`document_details.${index}.net_amount`]"
                              :error-messages="!!validation[`document_details.${index}.net_amount`] ? validation[`document_details.${index}.net_amount`] : []"
                              disabled
                            />
                          </td>
                          <td>
                            <v-text-field
                              v-model="detail.vat_type"
                              suffix="%"
                              type="number"
                              :append-icon="parseInt(detail.vat_type) === 0 && isIta ? 'mdi-pencil' : ''"
                              @click:append="openVatExclusion(index)"
                              :error="!!validation[`document_details.${index}.vat_type`] || !!validation[`document_details.${index}.vat_exclusion_it`]"
                              :error-messages="!!validation[`document_details.${index}.vat_type`] ? validation[`document_details.${index}.vat_type`] : []"
                              :disabled="isEditingDisabled"
                              dense
                            />
                          </td>
                          <td>
                            <v-text-field
                              v-model="detail.amount"
                              type="number"
                              :error="!!validation[`document_details.${index}.amount`]"
                              :error-messages="!!validation[`document_details.${index}.amount`] ? validation[`document_details.${index}.amount`] : []"
                              :suffix="form.currency"
                              :disabled="isEditingDisabled"
                              dense
                            />
                          </td>
                          <td>
                            <div class="text-right">
                              <v-btn icon small @click="addDetail()" v-if="index === form.document_details.length - 1">
                                <v-icon color="success">mdi-plus</v-icon>
                              </v-btn>
                              <v-btn icon small v-if="form.document_details.length > 1" @click="removeDetail(index)">
                                <v-icon color="error">mdi-minus</v-icon>
                              </v-btn>
                              <v-btn icon small @click="openRowDetail(detail, index)">
                                <v-icon>mdi-dots-vertical</v-icon>
                              </v-btn>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </v-tab-item>
                <v-tab-item value="tab-payments">
                  <v-card flat>
                    <v-card-actions>
                      <v-spacer></v-spacer>
                      <v-btn color="primary" small @click="addPayment()">
                        <v-icon>mdi-plus</v-icon>
                        {{ $lang('main.crud.add') }}
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                  <v-simple-table dense>
                    <template v-slot:default>
                      <thead>
                      <tr>
                        <th class="text-left" style="width: 13%">{{ $lang('payments.fields.date') }}</th>
                        <th class="text-left" style="width: 13%">{{ $lang('payments.fields.source') }}</th>
                        <th class="text-left" style="width: 13%">{{ $lang('payments.fields.type') }}</th>
                        <th class="text-left" style="width: 13%">{{ $lang('payments.fields.amount') }}</th>
                        <th class="text-left" style="width: 30%">{{ $lang('payments.fields.description') }}</th>
                        <th class="text-left" style="width: 8%">{{ $lang('payments.fields.is_deposit') }}</th>
                        <th class="text-center" style="width: 10%">{{ $lang('main.crud.actions') }}</th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr v-for="(payment, index) in form.payments" :key="index">
                        <td>
                          <v-date
                            v-model="payment.date"
                            dense
                            :prepend-icon="false"
                            label=""
                            :error="!!validation[`payments.${index}.date`]"
                            :error-messages="!!validation[`payments.${index}.date`] ? validation[`payments.${index}.date`] : []"
                          ></v-date>
                        </td>
                        <td>
                          <c-payment-sources-autocomplete
                            v-model="payment.source_id"
                            dense
                            :company-id="form.company_id"
                            :error="!!validation[`payments.${index}.source_id`]"
                            :error-messages="!!validation[`payments.${index}.source_id`] ? validation[`payments.${index}.source_id`] : []"
                          />
                        </td>
                        <td>
                          <v-select
                            v-model="payment.type"
                            @input="calculateTotals()"
                            dense
                            :items="$in('ConstPaymentTypes')"
                            :error="!!validation[`payments.${index}.type`]"
                            :error-messages="!!validation[`payments.${index}.type`] ? validation[`payments.${index}.type`] : []"
                          />
                        </td>
                        <td>
                          <v-text-field
                            v-model="payment.amount_absolute"
                            @input="calculateTotals()"
                            dense
                            :error="!!validation[`payments.${index}.amount`]"
                            :error-messages="!!validation[`payments.${index}.amount`] ? validation[`payments.${index}.amount`] : []"
                          >
                          </v-text-field>
                        </td>
                        <td>
                          <v-text-field
                            v-model="payment.description"
                            dense
                            :error="!!validation[`payments.${index}.description`]"
                            :error-messages="!!validation[`payments.${index}.description`] ? validation[`payments.${index}.description`] : []"
                          >
                          </v-text-field>
                        </td>
                        <td>
                          <v-switch
                            v-model="payment.is_deposit"
                            :true-value="1"
                            :false-value="0"
                          >
                          </v-switch>
                        </td>
                        <td>
                          <div class="text-right">
                            <v-icon @click="addPayment()" v-if="index === form.payments.length - 1" color="success">mdi-plus</v-icon>
                            <v-icon @click="removePayment(index)" color="error">mdi-minus</v-icon>
                          </div>
                        </td>
                      </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </v-tab-item>
              </v-tabs-items>
              <v-simple-table dense>
                <template v-slot:default>
                  <tbody>
                    <tr>
                      <td>
                        <v-text-field
                          v-model="totals.net_amount"
                          readonly
                          :label="$lang('documents.fields.totals.net_amount')"
                          :suffix="form.currency"
                          :error="!!validation.net_amount"
                          :error-messages="!!validation.net_amount ? validation.net_amount : []"
                        />
                      </td>
                      <td>
                        <v-text-field
                          v-model="totals.vat_amount"
                          readonly
                          :label="$lang('documents.fields.totals.vat_amount')"
                          :suffix="form.currency"
                          :error="!!validation.vat_amount"
                          :error-messages="!!validation.vat_amount ? validation.vat_amount : []"
                        />
                      </td>
                      <td>
                        <v-text-field
                          v-model="totals.amount"
                          readonly
                          :label="$lang('documents.fields.totals.total')"
                          :suffix="form.currency"
                          :error="!!validation.amount"
                          :error-messages="!!validation.amount ? validation.amount : []"
                        />
                      </td>
                      <td>
                        <v-text-field
                          v-model="totals.paid_amount"
                          readonly
                          :label="$lang('documents.fields.totals.paid_amount')"
                          :suffix="form.currency"
                          :error="!!validation.paid_amount"
                          :error-messages="!!validation.paid_amount ? validation.paid_amount : []"
                        />
                      </td>
                      <td>
                        <v-text-field
                          v-model="totals.remaining_amount"
                          readonly
                          :label="$lang('documents.fields.totals.to_pay')"
                          :suffix="form.currency"
                          :error="!!validation.remaining_amount"
                          :error-messages="!!validation.remaining_amount ? validation.remaining_amount : []"
                        >
                          <template v-slot:prepend-inner>
                            <v-icon v-if="totals.remaining_amount == 0" color="green">mdi-check-circle</v-icon>
                            <v-icon v-if="totals.remaining_amount != 0" color="red">mdi-alert-decagram</v-icon>
                          </template>
                        </v-text-field>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
              <v-expansion-panels v-if="form.type === $const('ConstDocumentTypes', 'INVOICE')">
                <v-expansion-panel>
                  <v-expansion-panel-header>
                    {{ $lang('documents.fields.order.title') }}
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <v-simple-table dense>
                      <template v-slot:default>
                        <tbody>
                          <tr>
                            <td>
                              <v-date
                                v-model="form.order.date"
                                :label="$lang('documents.fields.order.date')"
                              />
                            </td>
                            <td>
                              <v-text-field
                                v-model="form.order.n"
                                :label="$lang('documents.fields.order.n')"
                              />
                            </td>
                            <td>
                              <v-text-field
                                v-model="form.order.contract_code"
                                :label="$lang('documents.fields.order.contract_code')"
                              />
                            </td>
                          </tr>
                        </tbody>
                      </template>
                    </v-simple-table>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
    </v-form>

    <v-dialog v-model="vatExclusion.isOpen" width="50vw">
      <v-card>
        <v-card-title>
          {{ $lang("bills.labels.vat_exclusion") }}
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" xs="12" md="12">
              <v-select
                v-if="isItaDocument"
                v-model="vatExclusion.value"
                :items="$in('ConstInvoiceVatExclusionIT')"
              >
              </v-select>
              <v-select
                v-if="form.type === $const('ConstDocumentTypes', 'RECEIPT')"
                v-model="vatExclusion.value"
                :items="$in('ConstVatExclusionIT')"
              >
              </v-select>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="vatExclusion.isOpen = false">
            {{ $lang("main.crud.cancel") }}
          </v-btn>
          <v-btn
            color="primary"
            @click="saveVatExclusion()"
          >
            {{ $lang("main.crud.save") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="isOpenRowDetail" width="500">
      <RowDetail
        v-if="isOpenRowDetail"
        :detail="activeDetail"
        @save="saveRowDetail"
        @close="closeRowDetail"
      />
    </v-dialog>
  </v-card>
</template>

<script>
import { mapState } from "vuex";
import MixpanelEvents from "@/constants/events";

import {
  UPDATE_DOCUMENTS_RESET,
  UPDATE_DOCUMENTS,
  GET_DOCUMENTS_SINGLE,
} from "@/store/documents/actions";

import form from "@/pages/documents/mixins/form";

export default {
	name: "EditingForm",
	mixins: [
		form
	],
  props: {
	  itemId: {
	    type: Number,
      required: true
    },
  },
  data() {
    return {
      isEditingDisabled: true,
    }
  },
  beforeMount() {
	  this.$store.dispatch(GET_DOCUMENTS_SINGLE, {
	    id: this.itemId,
      params: {
	      _data: {
          selects: ['*'],
          relations: {
            client: {
              selects: ['clients.*']
            },
            payments: {
              selects: ['payments.*']
            },
            document_details: {
              selects: ['*']
            },
            company: {
              selects: [
                'id',
                'name',
                'country',
                'currency',
              ],
              relations: {
                series: {
                  selects: [
                    'company_id',
                    'n',
                    'series',
                    'type',
                    'is_default',
                  ]
                }
              }
            }
          }
        }
      }
    }).then((response) => {
      response.company.series.map((item) => {
        if (!item.series) {
          return;
        }

        if (item.type === this.$const('ConstCompanySeriesTypes', 'INVOICE')) {
          this.invoiceSeries.push({
            n: item.n,
            text: item.series,
            value: item.series,
          });
        }
        if (item.type === this.$const('ConstCompanySeriesTypes', 'RECEIPT')) {
          this.receiptSeries.push({
            n: item.n,
            text: item.series,
            value: item.series,
          });
        }
      })

      this.isIta = response.company.country === 'IT';
      this.companyName = response.company.name;
      this.setEditing;
      this.setStampAvailable();
    });
  },
	methods: {
    setEditing() {
      if (!this.form.status) {
        this.isEditingDisabled = false;
      }

      if (
        this.form.status === this.$const('ConstDocumentStatuses', 'XML_DOWNLOADED') ||
        this.form.status === this.$const('ConstDocumentStatuses', 'RECEIPT_SENT') ||
        this.form.status === this.$const('ConstDocumentStatuses', 'RECEIPT_ABORT')
      ) {
        this.isEditingDisabled = true;
      }
    },
		resetAction() {
			return UPDATE_DOCUMENTS_RESET;
		},
		submitAction() {
			return UPDATE_DOCUMENTS;
    },
    populate(item) {
      let data = { ...item };

      this.client = data.client;

      this.documentNumber = this.$documentFormat(data.document_n, data.document_series);

      if (!!data.payments && !!data.payments.length) {
        data.payments.map((payment) => {
          if (!payment.amount) {
            return false;
          }
          
          let type = payment.amount > 0 ? 'CASH_IN' : 'CASH_OUT';
          payment.type = this.$const('ConstPaymentTypes', type);
          payment.amount_absolute =  this.$absolute(payment.amount);
        });
      }

      delete data['client'];
      this.form.is_stamp_active = !!this.form.stamp_amount;
      data.order = data.order ? data.order : {};

      this.form = {
        ...this.form,
        ...data,
      };
    },
    track() {
      this.$monitorService.track(MixpanelEvents.FISCAL_DOC_EDITED);
    },
	},
	computed: {
		...mapState({
      item: state => state.documents.getSingleRequest.item,
      dataLoading: state => state.documents.getSingleRequest.loading,
			loading: state => state.documents.editingRequest.loading,
			validation: state => state.documents.editingRequest.validation,
    }),
    initialized() {
        return this.dataLoading && this.loading;
    }
	}
};
</script>

<style scoped>
.v-input--is-disabled:not(.v-input--is-readonly) {
    pointer-events: auto;
}
</style>